/**
 * Şube CRUD servisi.
 * Firestore 'subeler' koleksiyonu üzerinde işlemler.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import type { Sube } from '@/types'

const SUBELER = 'subeler'

/**
 * Yeni doküman ID üretir (Firestore otomatik).
 */
function yeniSubeId(): string {
  const db = getFirestoreDb()
  return doc(collection(db, SUBELER)).id
}

/**
 * Tüm şubeleri listele (aktif + pasif).
 */
export async function tumSubeleriGetir(): Promise<Sube[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, SUBELER), orderBy('isim'))
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Sube)
}

/**
 * Tek şube getir.
 */
export async function subeGetir(id: string): Promise<Sube | null> {
  const db = getFirestoreDb()
  const docSnap = await getDoc(doc(db, SUBELER, id))
  if (!docSnap.exists()) return null
  return { id: docSnap.id, ...docSnap.data() } as Sube
}

/**
 * Yeni şube oluştur.
 */
export async function subeOlustur(
  data: Omit<Sube, 'id' | 'olusturmaTarihi' | 'aktif'> & { aktif?: boolean }
): Promise<string> {
  const db = getFirestoreDb()
  const id = yeniSubeId()

  await setDoc(doc(db, SUBELER, id), {
    ...data,
    aktif: data.aktif ?? true,
    olusturmaTarihi: nowServer(),
  })

  return id
}

/**
 * Şubeyi güncelle.
 */
export async function subeGuncelle(
  id: string,
  data: Partial<Omit<Sube, 'id' | 'olusturmaTarihi'>>
): Promise<void> {
  const db = getFirestoreDb()
  await updateDoc(doc(db, SUBELER, id), data)
}

/**
 * Şubeyi soft-delete (aktif: false).
 * Spec madde 16 — kayıtlar korunur, sadece gizlenir.
 */
export async function subeSil(id: string): Promise<void> {
  const db = getFirestoreDb()
  await updateDoc(doc(db, SUBELER, id), { aktif: false })
}

/**
 * Pasif şubeyi geri aktif et.
 */
export async function subeGeriYukle(id: string): Promise<void> {
  const db = getFirestoreDb()
  await updateDoc(doc(db, SUBELER, id), { aktif: true })
}

/**
 * Helper: Timestamp olarak şu anı döner (test/debug için).
 */
export function _simdi(): Timestamp {
  return Timestamp.now()
}
